import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Stack,
  Switch,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material'
import { useOutletContext, useNavigate, useParams } from 'react-router-dom'
import { useAppTheme } from '@/theme/ThemeContext'
import { AlertItem, PRIORITY_BY_KEY } from '@/mockdata/notification/notifications'
import AlertMessageCard from '@/components/NotificationManagment/AlertMessageCard'
import EditAlertTextDialog from '@/components/NotificationManagment/EditAlertTextDialog'
import CloseOpenAlertsDialog, {
  type CloseOpenAlertsMode,
} from '@/components/NotificationManagment/CloseOpenAlertsDialog'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { FiberManualRecord } from '@mui/icons-material'
import dayjs from 'dayjs'
import {
  APIAlertTypeDetails,
  cloneAlertType,
  deleteAlertType,
  listAlertTypesForManage,
  refreshAlertTypeCounts,
  setAlertTypeEnabled,
} from '@/services/notificationService'
import {
  REFRESH_DONE,
  refreshFailure,
  type RefreshOutcome,
} from './candidateCount'
import {
  severityBucketFromHint,
  severityHebrewFromHint,
} from '@/utils/severity'

function toAlertItem(d: APIAlertTypeDetails): AlertItem {
  const sev = d.severityHint
  return {
    id: d.alertTypeId,
    title: d.alertTypeName,
    severity: severityHebrewFromHint(sev),
    kpi: d.candidateCount ?? 0,
    kpiComputedAt: d.candidateCountComputedAt,
    text: d.description ?? '',
    tags: (d.tags ?? []).map(t => t.tagName),
    priority: severityBucketFromHint(sev),
    createdAt: d.createdAt ? dayjs(d.createdAt).format('DD/MM/YYYY') : '',
    changedAt: d.updatedAt ? dayjs(d.updatedAt).format('DD/MM/YYYY') : '',
    createdBy: d.createdBy ?? '',
    isEnabled: d.isEnabled,
    isPinned: false,
  }
}

// The Gateway counts a type a few seconds after it is enabled or copied, so the page asks again
// for a short while instead of leaving the card on "not calculated yet".
const FOLLOW_UP_MS = 3000
const FOLLOW_UP_TRIES = 8

interface ContextType {
  search: string
}

// ── Disabled item row (Premium matching Figma) ────────────────────────────────
function DisabledItemRow({
  item,
  onReEnable,
  onDuplicate,
  isDarkMode,
}: {
  item: AlertItem
  onReEnable: (id: number) => void
  onDuplicate: (id: number) => void
  isDarkMode: boolean
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const pr = PRIORITY_BY_KEY[item.priority]
  const dotColor = pr?.color ?? '#C0C2D6'
  const textColor = isDarkMode ? '#E2E8F0' : '#003F61'
  const subColor = isDarkMode ? '#64748B' : '#94A3B8'

  return (
    <Box
      sx={{
        height: 50,
        borderRadius: '8px',
        // Translucent dark background matching the premium UI look
        bgcolor: isDarkMode ? 'rgba(23, 33, 55, 0.8)' : 'rgba(248, 250, 252, 0.8)',
        border: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
        px: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        backdropFilter: 'blur(10px)',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(241, 245, 249, 0.95)',
        }
      }}
    >
      {/* Right side: Title + Severity Dot + Severity Text */}
      <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: 'Rubik, sans-serif',
            fontSize: '13px',
            fontWeight: 500,
            color: textColor,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {item.title}
        </Typography>
        <FiberManualRecord sx={{ fontSize: 6, color: dotColor, flexShrink: 0 }} />
        <Typography
          sx={{
            fontFamily: 'Rubik, sans-serif',
            fontSize: '12px',
            fontWeight: 400,
            color: subColor,
            flexShrink: 0,
          }}
        >
          {`חומרה ${item.severity || 'רגילה'}`}
        </Typography>
      </Stack>

      {/* Left side: Menu (Far Left) and Toggle */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
        <Switch
          size="small"
          checked={false}
          onChange={() => onReEnable(item.id)}
          sx={{ 
            transform: 'scale(0.8)',
            '& .MuiSwitch-switchBase': { color: '#94A3B8' },
            '& .MuiSwitch-track': { bgcolor: '#334155' }
          }}
        />
        <IconButton
          size="small"
          onClick={e => setAnchorEl(e.currentTarget)}
          sx={{ color: subColor, p: 0.5 }}
        >
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            sx: {
              bgcolor: isDarkMode ? '#1E293B' : '#FFFFFF',
              color: textColor,
              borderRadius: 2,
              minWidth: 120,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              border: isDarkMode ? '1px solid #334155' : 'none',
            },
          }}
        >
          <MenuItem
            onClick={() => { onReEnable(item.id); setAnchorEl(null) }}
            sx={{ fontSize: 13, fontFamily: 'Rubik, sans-serif' }}
          >
            הפעל התראה
          </MenuItem>
          <MenuItem
            onClick={() => {
              onDuplicate(item.id)
              setAnchorEl(null)
            }}
            sx={{ fontSize: 13, fontFamily: 'Rubik, sans-serif' }}
          >
            שכפול
          </MenuItem>
        </Menu>
      </Stack>
    </Box>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────
const NotificationManage: React.FC = () => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const { search = '' } = useOutletContext<ContextType>()
  const navigate = useNavigate()
  const { siteId } = useParams()
  
  const [alerts, setAlerts] = useState<AlertItem[]>([])
  const [loading, setLoading] = useState(true)
  // Severity filter state: 'high' | 'medium' | 'low' | null
  const [severityFilter, setSeverityFilter] = useState<string | null>(null)

  // Floating Modal state
  const [disabledOpen, setDisabledOpen] = useState(false)

  // Alert currently open in the "עריכת מלל וכותרת" dialog, or null when closed.
  const [editingAlert, setEditingAlert] = useState<AlertItem | null>(null)

  // Alert waiting for the answer to "close its open alerts?", with the action that asked.
  const [closing, setClosing] = useState<{
    alert: AlertItem
    mode: CloseOpenAlertsMode
  } | null>(null)

  const [refreshing, setRefreshing] = useState(false)
  const [refreshOutcome, setRefreshOutcome] = useState<RefreshOutcome | null>(
    null
  )
  const [followUpsLeft, setFollowUpsLeft] = useState(0)

  const fetchAlerts = useCallback(
    async (quiet = false) => {
      if (!siteId) return
      if (!quiet) setLoading(true)
      try {
        const [enabledRes, disabledRes] = await Promise.all([
          listAlertTypesForManage(siteId, {
            page: 1,
            size: 200,
            enabled: true,
          }),
          listAlertTypesForManage(siteId, {
            page: 1,
            size: 200,
            enabled: false,
          }),
        ])
        const merged = [...enabledRes.result, ...disabledRes.result].map(
          toAlertItem
        )
        setAlerts(merged)
      } catch (err) {
        console.error('[NotificationManage] Failed to fetch alert types:', err)
      } finally {
        setLoading(false)
      }
    },
    [siteId]
  )

  useEffect(() => {
    fetchAlerts()
  }, [fetchAlerts])

  const enabledAlerts = useMemo(() => alerts.filter(a => a.isEnabled), [alerts])

  useEffect(() => {
    if (followUpsLeft <= 0) return
    if (!enabledAlerts.some(a => a.kpiComputedAt === null)) {
      setFollowUpsLeft(0)
      return
    }
    const timer = setTimeout(() => {
      setFollowUpsLeft(n => n - 1)
      fetchAlerts(true)
    }, FOLLOW_UP_MS)
    return () => clearTimeout(timer)
  }, [followUpsLeft, enabledAlerts, fetchAlerts])

  const handleRefreshCounts = async () => {
    if (!siteId || refreshing) return
    setRefreshing(true)
    setRefreshOutcome(null)
    try {
      await refreshAlertTypeCounts(siteId)
      setRefreshOutcome(REFRESH_DONE)
    } catch (err) {
      setRefreshOutcome(refreshFailure(err))
    } finally {
      setRefreshing(false)
    }
    await fetchAlerts(true)
  }
  const disabledAlerts = useMemo(() => alerts.filter(a => !a.isEnabled), [alerts])

  const filteredAlerts = useMemo(() => {
    let result = enabledAlerts

    // Apply Search Filter
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(alert =>
        alert.title.toLowerCase().includes(q) ||
        alert.text.toLowerCase().includes(q) ||
        String(alert.id).includes(q)
      )
    }

    // Apply Severity Filter
    if (severityFilter) {
      result = result.filter(alert => {
        if (severityFilter === 'high') return alert.severity === 'גבוהה' || alert.severity === 'גבוהה מאוד'
        if (severityFilter === 'medium') return alert.severity === 'בינונית'
        if (severityFilter === 'low') return alert.severity === 'קלה' || alert.severity === 'נמוכה'
        return true
      })
    }

    return result
  }, [enabledAlerts, search, severityFilter])

  const handleToggleSeverityFilter = (sev: string) => {
    setSeverityFilter(prev => prev === sev ? null : sev)
  }

  const handleToggleEnabled = async (id: number, enabled: boolean) => {
    if (!enabled) {
      const alert = alerts.find(a => a.id === id)
      if (alert) setClosing({ alert, mode: 'disable' })
      return
    }
    // Optimistic update
    setAlerts(prev => prev.map(a => (a.id === id ? { ...a, isEnabled: enabled } : a)))
    try {
      if (!siteId) return
      const updated = await setAlertTypeEnabled(siteId, id, enabled)
      setAlerts(prev => prev.map(a => (a.id === id ? toAlertItem(updated) : a)))
      if (enabled) {
        setFollowUpsLeft(FOLLOW_UP_TRIES)
        await fetchAlerts(true)
      }
    } catch (err) {
      console.error('[NotificationManage] Toggle failed, reverting:', err)
      setAlerts(prev => prev.map(a => (a.id === id ? { ...a, isEnabled: !enabled } : a)))
    }
  }

  const handleReEnable = (id: number) => handleToggleEnabled(id, true)

  const clearDisabledAlerts = async () => {
    if (!siteId) return
    const ids = disabledAlerts.map(a => a.id)
    // Optimistic removal
    setAlerts(prev => prev.filter(a => a.isEnabled))
    try {
      await Promise.all(ids.map(id => deleteAlertType(siteId, id)))
    } catch (err) {
      console.error('[NotificationManage] Bulk delete failed, refetching:', err)
      fetchAlerts()
    }
  }

  const handleDuplicate = async (id: number) => {
    if (!siteId) return
    try {
      const created = await cloneAlertType(siteId, id)
      setAlerts(prev => [toAlertItem(created), ...prev])
      // The Gateway makes a copy disabled, so it lands in the disabled list, not in the grid.
      setRefreshOutcome({
        kind: 'done',
        message: `נוצר ההעתק "${created.alertTypeName}". הוא כבוי ונמצא ב"התראות כבויות"`,
      })
      setFollowUpsLeft(FOLLOW_UP_TRIES)
      await fetchAlerts(true)
    } catch (err) {
      console.error('[NotificationManage] Clone failed:', err)
      setRefreshOutcome({ kind: 'failed', message: 'השכפול נכשל' })
    }
  }

  const handleDelete = (id: number) => {
    const alert = alerts.find(a => a.id === id)
    if (alert) setClosing({ alert, mode: 'delete' })
  }

  const confirmClosing = async (closeOpenAlerts: boolean) => {
    if (!siteId || !closing) return
    const { alert, mode } = closing
    if (mode === 'disable') {
      const updated = await setAlertTypeEnabled(
        siteId,
        alert.id,
        false,
        closeOpenAlerts
      )
      setAlerts(prev =>
        prev.map(a => (a.id === alert.id ? toAlertItem(updated) : a))
      )
    } else {
      await deleteAlertType(siteId, alert.id)
      setAlerts(prev => prev.filter(a => a.id !== alert.id))
    }
    setClosing(null)
  }

  const handleEdit = (id: number) => {
    const alert = alerts.find(a => a.id === id)
    if (alert) setEditingAlert(alert)
  }

  const bg = isDarkMode ? '#091327' : '#F6FAFF'
  const textColor = isDarkMode ? '#FFFFFF' : '#003F61'
  const subColor = isDarkMode ? '#94A3B8' : '#64748B'

  return (
    <Box
      dir="rtl"
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: bg,
        overflow: 'hidden',
      }}
    >
      {/* ── Main Grid Container (Blurred when Open) ── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          px: 3, pt: 3, pb: 3,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          filter: disabledOpen ? 'blur(6px)' : 'none',
          opacity: disabledOpen ? 0.7 : 1,
          pointerEvents: disabledOpen ? 'none' : 'auto',
        }}
      >
        {/* Top Controls: Add Button (Floating visually at top left) */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
          <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/${siteId}/notifications/manage-notifications/new/flow-chart`)}
              sx={{
                fontFamily: 'Rubik, sans-serif',
                fontWeight: 600,
                fontSize: '14px',
                bgcolor: '#72A4EB',
                color: '#FFFFFF',
                borderRadius: '6px',
                textTransform: 'none',
                boxShadow: 'none',
                px: 3,
                py: 0.75,
                '&:hover': { bgcolor: '#5A8FD4', boxShadow: 'none' },
              }}
            >
              התראה
            </Button>
            <Button
              variant="outlined"
              onClick={handleRefreshCounts}
              disabled={refreshing}
              startIcon={
                refreshing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RefreshIcon />
                )
              }
              sx={{
                fontFamily: 'Rubik, sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                color: '#72A4EB',
                borderColor: '#72A4EB',
                borderRadius: '6px',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                px: 2,
                py: 0.75,
              }}
            >
              {refreshing ? 'מרענן ספירה…' : 'רענון ספירה'}
            </Button>
            {refreshOutcome && (
              <Typography
                role="status"
                sx={{
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '13px',
                  color: refreshOutcome.kind === 'done' ? '#2E7D32' : '#C62828',
                }}
              >
                {refreshOutcome.message}
              </Typography>
            )}
            <Stack direction="row" gap={1.5} alignItems="center">
            <Box
              onClick={() => handleToggleSeverityFilter('high')}
              sx={{
                width: 14, height: 14, borderRadius: '50%', bgcolor: '#FE595A',
                cursor: 'pointer', transition: 'all 0.2s',
                border: severityFilter === 'high' ? '2px solid #FFF' : 'none',
                boxShadow: severityFilter === 'high' ? '0 0 10px rgba(254, 89, 90, 0.6)' : 'none',
                '&:hover': { transform: 'scale(1.2)' }
              }}
              title="חומרה גבוהה"
            />
            <Box
              onClick={() => handleToggleSeverityFilter('medium')}
              sx={{
                width: 14, height: 14, borderRadius: '50%', bgcolor: '#F79268',
                cursor: 'pointer', transition: 'all 0.2s',
                border: severityFilter === 'medium' ? '2px solid #FFF' : 'none',
                boxShadow: severityFilter === 'medium' ? '0 0 10px rgba(247, 146, 104, 0.6)' : 'none',
                '&:hover': { transform: 'scale(1.2)' }
              }}
              title="חומרה בינונית"
            />
            <Box
              onClick={() => handleToggleSeverityFilter('low')}
              sx={{
                width: 14, height: 14, borderRadius: '50%', bgcolor: '#FEC763',
                cursor: 'pointer', transition: 'all 0.2s',
                border: severityFilter === 'low' ? '2px solid #FFF' : 'none',
                boxShadow: severityFilter === 'low' ? '0 0 10px rgba(254, 199, 99, 0.6)' : 'none',
                '&:hover': { transform: 'scale(1.2)' }
              }}
              title="חומרה קלה"
            />
          </Stack>
          </Stack>
        </Box>

        {/* Grid Area */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, py: 8 }}>
            <CircularProgress size={36} sx={{ color: '#72A4EB' }} />
          </Box>
        ) : filteredAlerts.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))',
              gap: '16px'
            }}
          >
            {filteredAlerts.map(alert => (
              <AlertMessageCard
                key={alert.id}
                item={alert}
                enabled={alert.isEnabled}
                onToggleEnabled={enabled => handleToggleEnabled(alert.id, enabled)}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', height: '100%', alignItems: 'center', opacity: 0.7 }}>
            <Typography sx={{ fontFamily: 'Rubik, sans-serif', fontSize: '18px', color: subColor }}>
              לא נמצאו התראות
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Backdrop for Open State ── */}
      {disabledOpen && (
        <Box 
          onClick={() => setDisabledOpen(false)} 
          sx={{ position: 'absolute', inset: 0, zIndex: 9, cursor: 'pointer' }} 
        />
      )}

      {/* ── Floating Overlay (Bottom-Left) ── */}
      <Box
        sx={{
          width: { xs: 320, sm: 380 },
          display: 'flex',
          flexDirection: 'column',
          zIndex: 10,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          ...(disabledOpen
            ? {
                // Open state floats over the grid and pushes up
                position: 'absolute',
                left: 40,
                top: 50,
                bottom: 30,
              }
            : {
                // Closed state sits below the grid, so it never covers a card
                position: 'relative',
                flexShrink: 0,
                ml: '40px',
                mr: 'auto',
                mb: 3,
                height: 'auto',
              }),
        }}
      >
        {/* Header containing Title & Toggles */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} px={1}>
          {disabledOpen ? (
             <Stack direction="row" gap={2} alignItems="center">
                <Typography 
                  onClick={() => setDisabledOpen(false)} 
                  sx={{ fontFamily: 'Rubik', fontSize: '13px', color: '#72A4EB', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                >
                  הסתר
                </Typography>
                <Stack 
                  direction="row" 
                  alignItems="center" 
                  gap={0.5} 
                  onClick={clearDisabledAlerts}
                  sx={{ cursor: 'pointer', color: subColor, '&:hover': { color: '#C62828' } }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  <Typography sx={{ fontFamily: 'Rubik', fontSize: '13px' }}>רוקן הכל</Typography>
                </Stack>
             </Stack>
          ) : (
             <Typography
                onClick={() => setDisabledOpen(true)}
                sx={{
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '13px',
                  color: '#72A4EB',
                  cursor: 'pointer',
                  userSelect: 'none',
                  textDecoration: 'underline',
                  textUnderlineOffset: '4px',
                  fontWeight: 500,
                  '&:hover': { opacity: 0.8 },
                }}
             >
                הצג
             </Typography>
          )}

          <Typography
            sx={{
              fontFamily: 'Rubik, sans-serif',
              fontSize: '16px',
              fontWeight: 500,
              color: textColor,
            }}
          >
            התראות כבויות
          </Typography>
        </Stack>

        {/* List Content */}
        <Box 
           sx={{ 
             flex: disabledOpen ? 1 : 'none', 
             overflowY: disabledOpen ? 'auto' : 'visible', 
             position: 'relative',
             px: 1,
             // Height adjustment for stacked look
             height: disabledOpen ? 'auto' : '90px',
           }}
        >
          <Stack 
            spacing={disabledOpen ? 1.5 : 0} 
            sx={{ 
              pb: disabledOpen ? 2 : 0,
              position: 'relative',
              height: '100%'
            }}
          >
            {disabledAlerts.length > 0 ? (
              disabledAlerts.slice(0, disabledOpen ? undefined : 3).map((item, index) => (
                <Box
                  key={item.id}
                  sx={{
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    ...(disabledOpen ? {} : {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      zIndex: 10 - index,
                      transform: `translateY(${index * 8}px) scale(${1 - index * 0.04})`,
                      opacity: 1 - index * 0.2,
                      pointerEvents: index === 0 ? 'auto' : 'none',
                    })
                  }}
                >
                  <DisabledItemRow
                    item={item}
                    onReEnable={handleReEnable}
                    onDuplicate={handleDuplicate}
                    isDarkMode={isDarkMode}
                  />
                </Box>
              ))
            ) : (
              <Typography sx={{ fontSize: 13, color: subColor, textAlign: 'center', py: 4 }}>
                אין התראות כבויות
              </Typography>
            )}
          </Stack>
        </Box>

        {/* Bottom Close Button (Only visible when open) */}
        {disabledOpen && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, flexShrink: 0 }}>
            <IconButton 
              onClick={() => setDisabledOpen(false)} 
              sx={{ 
                color: isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                '&:hover': { color: textColor }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      {siteId && closing && (
        <CloseOpenAlertsDialog
          open
          mode={closing.mode}
          siteId={siteId}
          alertTypeId={closing.alert.id}
          alertTypeName={closing.alert.title}
          onConfirm={confirmClosing}
          onCancel={() => setClosing(null)}
        />
      )}

      {siteId && editingAlert && (
        <EditAlertTextDialog
          open
          onClose={() => setEditingAlert(null)}
          siteId={siteId}
          alertTypeId={editingAlert.id}
          initialTitle={editingAlert.title}
          initialDescription={editingAlert.text}
          onSaved={updated => {
            setAlerts(prev =>
              prev.map(a =>
                a.id === updated.alertTypeId ? toAlertItem(updated) : a
              )
            )
            setEditingAlert(null)
          }}
        />
      )}
    </Box>
  )
}

export default NotificationManage
